import { columnTitle, roleColumnDataIndex } from './lib/util';
import Column from 'antd/lib/table/Column';
import ColumnTitle from './ColumnTitle';
import type {
  RosterTableRowRecord,
  RosterUserRecord,
  Shift,
  ShiftPeriod,
  ViolationColumnHeader,
} from './types';
import ViolationCell from './ViolationCell';
import { defaultDayShiftPeriod } from '../Teams/TeamEditor/util';
import rfsTheme from '../../theme';
import { Col, Row, Table, TablePaginationConfig } from 'antd';
import differenceInDays from 'date-fns/differenceInDays';
import type {
  FatigueViolation,
  UserFatigueViolationReport,
} from '../../hooks/types';
import { addDays, format, isToday, isWeekend } from 'date-fns';
import { shiftDateDataIndexFormat } from '../../lib/utils/date-time';
import { relatedNotesToRelatedNotesWithCreatedAtAndUpdatedAtAsNumber } from '../Notes/util';

const rosterCellBackgroundColour = (col: ViolationColumnHeader) => {
  if (col.isToday) {
    return rfsTheme.darkRfsRedBackground;
  }

  if (col.isWeekend) {
    return rfsTheme.rosterWeekendShade;
  }

  return 'none';
};

const rosterCellMinWidth = (col: ViolationColumnHeader) => {
  if (col.dataIndex === roleColumnDataIndex) {
    return '250px';
  }

  return '85px';
};

type ViolationTableProps = {
  readonly tableHeaderOffset?: number;
  readonly loading: boolean;
  readonly rosterShiftsRecords: readonly RosterTableRowRecord[];
  readonly rosterPaginationSetting: false | TablePaginationConfig;
  readonly dayShiftPeriod?: ShiftPeriod;
  readonly usersFatigueReport?: readonly UserFatigueViolationReport[];
};

const violationPeriodStartAndEnd = (
  usersFatigueReport?: readonly UserFatigueViolationReport[]
) => {
  if (usersFatigueReport === undefined) {
    return [];
  }

  const violationTableDates: readonly number[] = usersFatigueReport.flatMap(
    (violationReport) =>
      violationReport.userShifts.flatMap((userShift) => [
        new Date(userShift.startTime).valueOf(),
        new Date(userShift.endTime).valueOf(),
      ])
  );

  const firstDate = Math.min(...violationTableDates);
  const lastDate = Math.max(...violationTableDates);

  return [new Date(firstDate), new Date(lastDate)] as const;
};

const shiftsWithViolations = (
  reports: readonly FatigueViolation[]
): ReadonlySet<string> => {
  const allIds: readonly string[] = reports
    .filter(({ report: { violated } }) => violated)
    .flatMap((report) => report.report.shifts.map((shift) => shift.id));

  return new Set(allIds);
};

export type ShiftWithViolations = {
  readonly consecutiveShiftViolation: boolean;
  readonly maxShiftHoursViolation: boolean;
  readonly shiftRestGapViolation: boolean;
  readonly teamRoleName?: string;
  readonly team?: {
    readonly id?: string;
    readonly name?: string;
  };
} & Shift;

export type ShiftViolationTableRecord = {
  readonly userId: string;
  readonly userRecord?: RosterUserRecord;
  readonly shifts: Record<string, readonly ShiftWithViolations[]>;
};

/**
 * Returns the column headers for a violation table.
 */
export const violationHeaders = (
  rosterDates: readonly Date[]
): readonly ViolationColumnHeader[] => {
  // For single users, the tables are stacked; each roster has a row of dates.
  // A future improvement may be to sync the scrollbars together.
  const firstColumnHeader: ViolationColumnHeader = {
    title: (
      <Row wrap={false}>
        <Col flex="auto">Member with Violation</Col>
      </Row>
    ),
    dataIndex: roleColumnDataIndex,
    isToday: false,
    isWeekend: false,
    isLastColumn: false,
  };

  return [firstColumnHeader].concat(
    rosterDates.map((date, i, { length }) => ({
      title: columnTitle(date),
      isToday: isToday(date),
      isWeekend: isWeekend(date),
      dataIndex: format(date, shiftDateDataIndexFormat),
      isLastColumn: i + 1 === length,
    }))
  );
};
/**
 * Table to display all of a user's rostered shifts, with the shifts that
 * violate the fatigue rules highlighted in the table.
 *
 * The table shows the shifts for a user from every team that they
 * are rostered onto, as well as the date and time fo the shift.
 *
 * @param tableHeaderOffset pixels, the space between the header and table, used in personal and team modes
 * @param loading boolean, will show a spinner when loading
 * @param rosterShiftsRecords that are the data that will be shown in the table
 * @param rosterPaginationSetting to be passed down into the table
 * @param dayShiftPeriod the start, and end time of the day shift. Night shift is the other time
 * @param usersFatigueReport of shifts for users that violation the fatigue rules
 * @constructor
 */
export const ViolationTable = ({
  tableHeaderOffset,
  loading,
  rosterShiftsRecords,
  rosterPaginationSetting,
  dayShiftPeriod,
  usersFatigueReport,
}: ViolationTableProps) => {
  /**
   * Uses the shifts returned from the api to find the first and last
   * dates that have shifts, this takes into account the wider dates used in the
   * calculation of fatigue violations
   * Makes the column headers for that date range.
   */

  const [startDay, endDay] = violationPeriodStartAndEnd(usersFatigueReport);

  const daysToShow = differenceInDays(endDay, startDay);

  const dateArray: readonly Date[] = new Array(daysToShow)
    .fill(null)
    .map((_, i) => addDays(startDay, i + 1));

  const violationColumnHeaders = violationHeaders([startDay, ...dateArray]);

  // Pulls the usernames out of the roster information rather than doing a request for each
  const usersMap: Record<string, RosterUserRecord> = rosterShiftsRecords
    .flatMap((roster) => roster.users)
    .reduce((acc, user) => {
      if (user?.userId === undefined) {
        return acc;
      }

      return { ...acc, [user.userId]: user };
    }, {});

  // converts the userShift information into the form expected by the table
  const violationTableData: readonly ShiftViolationTableRecord[] | undefined =
    usersFatigueReport
      ?.map(
        ({
          userShifts,
          userId,
          consecutiveShiftViolations,
          maxShiftHoursViolations,
          shiftRestGapViolations,
        }) => {
          const consecutiveShiftIds: ReadonlySet<string> = shiftsWithViolations(
            consecutiveShiftViolations
          );
          const maxShiftIds: ReadonlySet<string> = shiftsWithViolations(
            maxShiftHoursViolations
          );
          const restShiftIds: ReadonlySet<string> = shiftsWithViolations(
            shiftRestGapViolations
          );

          return {
            userId,
            userRecord: usersMap[userId],
            shifts: userShifts
              .toSorted(
                (shift1, shift2) =>
                  new Date(shift1.startTime).getTime() -
                  new Date(shift2.startTime).getTime()
              )
              .map(({ id, ...shift }) => ({
                ...shift,
                id,
                consecutiveShiftViolation: consecutiveShiftIds.has(id),
                maxShiftHoursViolation: maxShiftIds.has(id),
                shiftRestGapViolation: restShiftIds.has(id),
              })),
          };
        }
      )
      .map((userShift) => {
        const shiftsByDay = userShift.shifts.reduce<
          Record<string, readonly ShiftWithViolations[]>
        >((acc, shift) => {
          const startDate = format(
            new Date(shift.startTime),
            shiftDateDataIndexFormat
          );
          const existing: readonly ShiftWithViolations[] = acc[startDate] ?? [];
          return {
            ...acc,
            [startDate]: [
              ...existing,
              {
                ...shift,
                relatedNotes:
                  relatedNotesToRelatedNotesWithCreatedAtAndUpdatedAtAsNumber(
                    shift.relatedNotes
                  ),
              },
            ],
          };
        }, {});

        return { ...userShift, shifts: shiftsByDay };
      })
      .filter((userShifts) => Object.keys(userShifts.shifts).length > 0);

  // Passed into the cell component to refresh the width effect of each cell
  const cellRefresh: readonly unknown[] = [
    daysToShow,
    violationTableData,
    violationColumnHeaders,
  ];

  return (
    <Table
      bordered
      sticky={{
        offsetHeader: tableHeaderOffset,
      }}
      className="roster-table"
      loading={loading}
      tableLayout="fixed"
      dataSource={violationTableData}
      rowKey={'userId'}
      pagination={rosterPaginationSetting}
      expandable={{
        defaultExpandAllRows: true,
        expandedRowKeys: rosterShiftsRecords.map((record) => record.key),
      }}
      scroll={{
        x: true,
      }}
    >
      {violationColumnHeaders.map((col, index) => {
        const isRoleHeader = col.dataIndex === roleColumnDataIndex;
        return (
          <Column
            key={index}
            align="left"
            title={
              <span>
                <ColumnTitle
                  className={
                    isRoleHeader ? 'role-col-header' : 'date-col-header'
                  }
                  title={col.title}
                  isToday={col.isToday}
                />
              </span>
            }
            onCell={() => ({
              style: {
                overflow: col.isLastColumn ? 'hidden' : undefined,
                background: rosterCellBackgroundColour(col),
                minWidth: rosterCellMinWidth(col),
              },
            })}
            render={(record: ShiftViolationTableRecord, _, idx) => (
              <ViolationCell
                key={`violation-cell-${idx}`}
                refresh={cellRefresh}
                rosterTableRowRecord={record}
                dataIndex={col.dataIndex}
                dayShift={dayShiftPeriod ?? defaultDayShiftPeriod}
              />
            )}
          />
        );
      })}
    </Table>
  );
};

export default ViolationTable;
